// @ts-nocheck
import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMyOrganization } from "@/hooks/useMyOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Pencil, Copy, Archive, ShieldCheck, Loader2, Search,
  FileText, Link as LinkIcon, FileUp, Trash2, GripVertical, ExternalLink, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor, RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import type { WaiverCatalogItem, WaiverField } from "@/types/events";

const ALL = "__all__";
const UNIVERSAL = "__universal__";

const DEFAULT_FIELDS: WaiverField[] = [
  { key: "player_name", label: "Player name", type: "text", required: true },
  { key: "player_dob", label: "Player date of birth", type: "date", required: true },
  { key: "guardian_name", label: "Parent/guardian name (if minor)", type: "text", required: false },
  { key: "emergency_contact_name", label: "Emergency contact name", type: "text", required: true },
  { key: "emergency_contact_phone", label: "Emergency contact phone", type: "phone", required: true },
  { key: "medical_conditions", label: "Medical conditions / allergies", type: "textarea", required: false },
  { key: "photo_release", label: "I consent to photo/video release", type: "checkbox", required: false },
];

const TEMPLATES: Record<string, { label: string; html: string }> = {
  standard: {
    label: "Standard sports waiver",
    html: `<h2>Assumption of Risk &amp; Release of Liability</h2>
<p>I, the undersigned, acknowledge that participation in athletic activities involves <strong>inherent risks</strong>, including but not limited to physical injury, property damage, and in rare cases, serious or permanent injury.</p>
<p>In consideration of being permitted to participate, I voluntarily assume all risks associated with participation and <strong>release, waive, and discharge</strong> the event organizers, sponsors, venues, and their respective officers, directors, employees, and volunteers from any and all liability, claims, demands, or causes of action arising out of participation.</p>
<p>I confirm that I (or my child, if a minor) am physically fit and have no medical condition that would prevent safe participation.</p>`,
  },
  photo: {
    label: "Photo / video release",
    html: `<h2>Photo &amp; Video Release</h2>
<p>I grant permission to the event organizers and their authorized partners to <strong>photograph, film, and record</strong> the participant during the event.</p>
<p>I understand these images and recordings may be used for promotional, educational, recruiting, or commercial purposes — including websites, social media, broadcast, and printed materials — <em>without compensation</em>.</p>
<p>I waive any right to inspect or approve the finished use of these materials.</p>`,
  },
  medical: {
    label: "Medical authorization",
    html: `<h2>Medical Treatment Authorization</h2>
<p>In the event of injury or illness during the event, I authorize event staff and licensed medical personnel to administer <strong>first aid and emergency medical treatment</strong> as deemed necessary.</p>
<p>I understand that reasonable efforts will be made to contact me or the listed emergency contact before non-emergency treatment.</p>
<p>I accept financial responsibility for any medical care provided, including ambulance transport, and agree to hold event organizers harmless for treatment decisions made in good faith.</p>`,
  },
  combined: {
    label: "Combined youth sports waiver",
    html: `<h2>Youth Sports Participation Agreement</h2>
<p>This document combines an <strong>assumption of risk</strong>, <strong>liability release</strong>, <strong>medical authorization</strong>, and <strong>photo/video consent</strong> for participation in youth athletic events.</p>

<h3>1. Assumption of Risk</h3>
<p>I understand that athletic participation carries inherent risks including but not limited to falls, contact with other participants, equipment failure, and other unforeseeable events that may result in <strong>injury, property damage, or in rare cases, serious permanent injury or death</strong>.</p>

<h3>2. Release of Liability</h3>
<p>In consideration of being permitted to participate, I, on behalf of myself and my minor child, hereby <strong>release, waive, and discharge</strong> the event organizers, host venues, sponsors, coaches, officials, and their employees and volunteers from any and all liability, claims, demands, actions, or causes of action arising from participation, even those caused by ordinary negligence.</p>

<h3>3. Medical Authorization</h3>
<p>I authorize event staff and qualified medical personnel to administer <strong>emergency medical treatment</strong> if I (or my child) am unable to give consent due to injury or incapacity. I accept financial responsibility for medical care provided.</p>

<h3>4. Photo &amp; Video Release</h3>
<p>I grant permission for the participant to be <strong>photographed, recorded, and broadcast</strong> during the event, and for those images and recordings to be used for promotional, recruiting, educational, and commercial purposes by the event organizers and their partners.</p>

<h3>5. Acknowledgment</h3>
<p>I have read this entire agreement, understand its terms, and sign it <strong>freely and voluntarily</strong>. If signing on behalf of a minor, I represent that I am the parent or legal guardian.</p>`,
  },
};

const EMPTY: Partial<WaiverCatalogItem> = {
  title: "",
  description: "",
  sport_id: null,
  waiver_type: "both",
  source_type: "text",
  body_html: "",
  pdf_storage_path: null,
  pdf_extracted_text: null,
  pdf_filename: null,
  pdf_size_bytes: null,
  external_url: null,
  signature_mode: "typed_name",
  required_fields: DEFAULT_FIELDS,
  version: 1,
  is_active: true,
};

export default function WaiverCatalogPage() {
  const { organization } = useMyOrganization();
  const orgId = organization?.id;

  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [items, setItems] = useState<WaiverCatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<"all" | "team" | "individual" | "both">("all");
  const [filterSport, setFilterSport] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<WaiverCatalogItem> | null>(null);
  const [previewing, setPreviewing] = useState<WaiverCatalogItem | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("sports").select("id, name").order("name");
      setSports(data ?? []);
    })();
  }, []);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("waiver_catalog")
      .select("*")
      .or(`organization_id.eq.${orgId},organization_id.is.null`)
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setItems((data ?? []) as WaiverCatalogItem[]);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (filterType !== "all" && i.waiver_type !== filterType) return false;
      if (filterSport !== ALL) {
        if (filterSport === UNIVERSAL && i.sport_id) return false;
        if (filterSport !== UNIVERSAL && i.sport_id !== filterSport) return false;
      }
      if (search.trim() && !i.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, filterType, filterSport, search]);

  const sportName = (id: string | null) =>
    id ? (sports.find(s => s.id === id)?.name ?? "—") : "Universal";

  const handleSave = async (draft: Partial<WaiverCatalogItem>) => {
    if (!draft.title?.trim()) { toast.error("Title is required"); return; }
    const payload: any = {
      organization_id: orgId,
      title: draft.title.trim(),
      description: draft.description ?? null,
      sport_id: draft.sport_id ?? null,
      waiver_type: draft.waiver_type ?? "both",
      source_type: draft.source_type ?? "text",
      body_html: draft.source_type === "text" ? (draft.body_html ?? "") : null,
      pdf_storage_path: draft.source_type === "pdf_upload" ? draft.pdf_storage_path : null,
      pdf_extracted_text: draft.source_type === "pdf_upload" ? draft.pdf_extracted_text : null,
      pdf_filename: draft.source_type === "pdf_upload" ? draft.pdf_filename : null,
      pdf_size_bytes: draft.source_type === "pdf_upload" ? draft.pdf_size_bytes : null,
      pdf_uploaded_at: draft.source_type === "pdf_upload" ? draft.pdf_uploaded_at : null,
      external_url: draft.source_type === "external_url" ? draft.external_url : null,
      signature_mode: draft.signature_mode ?? "typed_name",
      required_fields: draft.required_fields ?? [],
      is_active: draft.is_active !== false,
      version: draft.version ?? 1,
    };
    let res;
    if (draft.id) {
      res = await (supabase as any).from("waiver_catalog").update(payload).eq("id", draft.id);
    } else {
      res = await (supabase as any).from("waiver_catalog").insert(payload);
    }
    if (res.error) { toast.error(res.error.message); return; }
    toast.success(draft.id ? "Waiver updated" : "Waiver created");
    setEditing(null);
    load();
  };

  const duplicate = async (it: WaiverCatalogItem) => {
    const { id, created_at, updated_at, ...rest } = it as any;
    const { error } = await (supabase as any).from("waiver_catalog").insert({
      ...rest,
      title: `${it.title} (copy)`,
      version: 1,
      organization_id: orgId,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Waiver duplicated");
    load();
  };

  const archive = async (it: WaiverCatalogItem) => {
    if (!confirm(`Archive "${it.title}"?`)) return;
    const { error } = await (supabase as any)
      .from("waiver_catalog")
      .update({ is_active: false })
      .eq("id", it.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Archived");
    load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            Waivers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reusable waiver templates. Upload a PDF or write your own. Link to events when you create them.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="w-4 h-4 mr-1" /> New Waiver
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["all", "team", "individual", "both"] as const).map(t => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition ${
              filterType === t
                ? "bg-primary text-white border-primary"
                : "bg-background text-foreground border-border hover:bg-accent"
            }`}
          >
            {t === "all" ? "All" : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
        <select
          value={filterSport}
          onChange={(e) => setFilterSport(e.target.value)}
          className="ml-2 h-8 px-2 text-sm border border-input rounded-md bg-background"
        >
          <option value={ALL}>All sports</option>
          <option value={UNIVERSAL}>Universal</option>
          {sports.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-64 h-8"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No waivers yet. Create your first reusable waiver template.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(it => (
            <div key={it.id} className="border border-border rounded-lg p-4 bg-card flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-foreground">{it.title}</h3>
                <span className="text-xs text-muted-foreground">v{it.version}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Chip>{it.waiver_type === "both" ? "Team + Individual" : it.waiver_type[0].toUpperCase() + it.waiver_type.slice(1)}</Chip>
                <Chip>
                  {it.source_type === "pdf_upload" && <><FileText className="w-3 h-3 inline mr-0.5" /> PDF</>}
                  {it.source_type === "text" && <><Pencil className="w-3 h-3 inline mr-0.5" /> Text</>}
                  {it.source_type === "external_url" && <><LinkIcon className="w-3 h-3 inline mr-0.5" /> External</>}
                </Chip>
                <Chip>{sportName(it.sport_id)}</Chip>
                <Chip>
                  {it.signature_mode === "typed_name" && "Typed"}
                  {it.signature_mode === "drawn_signature" && "Drawn"}
                  {it.signature_mode === "click_to_sign" && "Click"}
                </Chip>
                {!it.is_active && <Chip className="bg-muted">Archived</Chip>}
              </div>
              {it.description && <p className="text-xs text-muted-foreground line-clamp-2">{it.description}</p>}
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border">
                <Button size="sm" variant="ghost" onClick={() => setPreviewing(it)}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(it)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(it)}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
                </Button>
                {it.is_active && (
                  <Button size="sm" variant="ghost" className="ml-auto text-destructive hover:text-destructive" onClick={() => archive(it)}>
                    <Archive className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <WaiverEditDialog
          waiver={editing}
          sports={sports}
          orgId={orgId}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {previewing && (
        <Dialog open onOpenChange={() => setPreviewing(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{previewing.title}</DialogTitle></DialogHeader>
            <WaiverPreview waiver={previewing} />
            <DialogFooter><Button variant="outline" onClick={() => setPreviewing(null)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Chip({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground border border-border text-xs ${className}`}>
      {children}
    </span>
  );
}

/* ───────── Edit dialog ───────── */
function WaiverEditDialog({
  waiver, sports, orgId, onClose, onSave,
}: {
  waiver: Partial<WaiverCatalogItem>;
  sports: { id: string; name: string }[];
  orgId?: string;
  onClose: () => void;
  onSave: (w: Partial<WaiverCatalogItem>) => void;
}) {
  const [draft, setDraft] = useState<Partial<WaiverCatalogItem>>(waiver);
  const [tab, setTab] = useState("basic");
  const [uploading, setUploading] = useState(false);

  const patch = (p: Partial<WaiverCatalogItem>) => setDraft(d => ({ ...d, ...p }));

  const insertTemplate = (key: string) => {
    const tpl = TEMPLATES[key];
    if (!tpl) return;
    patch({ body_html: (draft.body_html || "") + tpl.html });
  };

  const handlePdfUpload = async (file: File) => {
    if (file.type !== "application/pdf") { toast.error("Only PDF files allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("PDF must be under 10MB"); return; }
    setUploading(true);
    try {
      const wid = draft.id ?? crypto.randomUUID();
      const path = `${orgId ?? "org"}/${wid}-${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage.from("waiver-pdfs").upload(path, file, {
        cacheControl: "3600", upsert: true, contentType: "application/pdf",
      });
      if (upErr) throw upErr;

      // Extract text via pdfjs-dist
      let extracted = "";
      try {
        const pdfjs: any = await import("pdfjs-dist");
        // Use the worker bundled with pdfjs-dist via Vite ?url import
        // Fallback to disabling worker if Vite cannot resolve it
        try {
          const workerUrl = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
          pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        } catch {
          pdfjs.GlobalWorkerOptions.workerSrc = "";
        }
        const buf = await file.arrayBuffer();
        const doc = await pdfjs.getDocument({ data: buf, disableWorker: !pdfjs.GlobalWorkerOptions.workerSrc }).promise;
        const parts: string[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const tc = await page.getTextContent();
          parts.push(tc.items.map((it: any) => it.str).join(" "));
        }
        extracted = parts.join("\n\n");
      } catch (err) {
        console.warn("PDF text extraction failed", err);
      }

      patch({
        pdf_storage_path: path,
        pdf_filename: file.name,
        pdf_size_bytes: file.size,
        pdf_uploaded_at: new Date().toISOString(),
        pdf_extracted_text: extracted,
      });
      toast.success(extracted ? "PDF uploaded and parsed" : "PDF uploaded (text extraction unavailable)");
    } catch (e: any) {
      toast.error(e.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Edit Waiver" : "New Waiver"}</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="signature">Signature</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 pt-3">
            <div>
              <Label>Title *</Label>
              <Input value={draft.title ?? ""} onChange={(e) => patch({ title: e.target.value })} />
            </div>
            <div>
              <Label>Description (internal)</Label>
              <Textarea
                value={draft.description ?? ""}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="Notes for staff — not shown to signers"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <RadioGroup
                value={draft.waiver_type ?? "both"}
                onValueChange={(v) => patch({ waiver_type: v as any })}
                className="grid grid-cols-1 gap-2"
              >
                {[
                  { v: "team", t: "Team Waiver", d: "Signed once by team coach/director" },
                  { v: "individual", t: "Individual Waiver", d: "Signed by each player/parent" },
                  { v: "both", t: "Both", d: "Collect both team and individual signatures" },
                ].map(opt => (
                  <label key={opt.v} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${draft.waiver_type === opt.v ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
                    <RadioGroupItem value={opt.v} className="mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">{opt.t}</div>
                      <div className="text-xs text-muted-foreground">{opt.d}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>Sport</Label>
              <select
                value={draft.sport_id ?? ""}
                onChange={(e) => patch({ sport_id: e.target.value || null })}
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              >
                <option value="">Universal (all sports)</option>
                {sports.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2">
              <Checkbox checked={draft.is_active !== false} onCheckedChange={(v) => patch({ is_active: !!v })} />
              <span className="text-sm">Active</span>
            </label>
          </TabsContent>

          <TabsContent value="content" className="space-y-4 pt-3">
            <RadioGroup
              value={draft.source_type ?? "text"}
              onValueChange={(v) => patch({ source_type: v as any })}
              className="flex gap-4"
            >
              {[
                { v: "text", t: "Write text" },
                { v: "pdf_upload", t: "Upload PDF" },
                { v: "external_url", t: "External URL" },
              ].map(opt => (
                <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value={opt.v} />
                  <span className="text-sm">{opt.t}</span>
                </label>
              ))}
            </RadioGroup>

            {draft.source_type === "text" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Waiver text</Label>
                  <select
                    onChange={(e) => { if (e.target.value) { insertTemplate(e.target.value); e.target.value = ""; } }}
                    className="h-8 px-2 text-xs border border-input rounded-md bg-background"
                    defaultValue=""
                  >
                    <option value="">Insert template…</option>
                    {Object.entries(TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <RichTextEditor
                  value={draft.body_html ?? ""}
                  onChange={(html) => patch({ body_html: html })}
                  minHeight="280px"
                  placeholder="Write your waiver content. Standard youth sports waivers include: assumption of risk, medical authorization, photo release."
                />
              </div>
            )}

            {draft.source_type === "pdf_upload" && (
              <div className="space-y-3">
                <label className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-accent transition">
                  <FileUp className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Drop PDF here or click to browse</p>
                  <p className="text-xs text-muted-foreground">.pdf only, max 10MB</p>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePdfUpload(f); }}
                  />
                </label>
                {uploading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</div>}
                {draft.pdf_filename && (
                  <div className="border border-border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-medium">{draft.pdf_filename}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {((draft.pdf_size_bytes ?? 0) / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    {draft.pdf_extracted_text && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground">Extracted text preview</summary>
                        <pre className="mt-2 p-2 bg-muted rounded max-h-40 overflow-auto whitespace-pre-wrap">
                          {draft.pdf_extracted_text.slice(0, 1500)}
                          {draft.pdf_extracted_text.length > 1500 ? "…" : ""}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}

            {draft.source_type === "external_url" && (
              <div className="space-y-2">
                <Label>External waiver URL</Label>
                <Input
                  type="url"
                  placeholder="https://adobesign.com/..."
                  value={draft.external_url ?? ""}
                  onChange={(e) => patch({ external_url: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Use this when your waiver is hosted on another platform (e.g., Adobe Sign, DocuSign).
                  Signers will be redirected. <strong>Note:</strong> signatures collected externally won't appear in RLevents.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="signature" className="space-y-4 pt-3">
            <div>
              <Label>Signature mode *</Label>
              <RadioGroup
                value={draft.signature_mode ?? "typed_name"}
                onValueChange={(v) => patch({ signature_mode: v as any })}
                className="grid grid-cols-1 gap-2 mt-1"
              >
                {[
                  { v: "typed_name", t: "Typed name", d: "User types full legal name" },
                  { v: "drawn_signature", t: "Drawn signature", d: "User signs on a canvas (mobile-friendly)" },
                  { v: "click_to_sign", t: "Click to sign", d: "Single-click acceptance with typed name (legal-grade)" },
                ].map(opt => (
                  <label key={opt.v} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${draft.signature_mode === opt.v ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
                    <RadioGroupItem value={opt.v} className="mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">{opt.t}</div>
                      <div className="text-xs text-muted-foreground">{opt.d}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <WaiverFieldsEditor
              fields={draft.required_fields ?? []}
              onChange={(f) => patch({ required_fields: f })}
            />
          </TabsContent>

          <TabsContent value="preview" className="pt-3">
            <WaiverPreview waiver={draft as WaiverCatalogItem} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(draft)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────── Required-fields editor ───────── */
function WaiverFieldsEditor({ fields, onChange }: { fields: WaiverField[]; onChange: (f: WaiverField[]) => void }) {
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  const update = (i: number, p: Partial<WaiverField>) => {
    const next = [...fields];
    next[i] = { ...next[i], ...p };
    onChange(next);
  };
  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i));
  const add = () => onChange([
    ...fields,
    { key: `field_${fields.length + 1}`, label: "New field", type: "text", required: false },
  ]);
  const move = (from: number, to: number) => {
    const next = [...fields];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Additional required fields</Label>
        <Button size="sm" variant="outline" onClick={add}><Plus className="w-3.5 h-3.5 mr-1" /> Add field</Button>
      </div>
      <div className="space-y-1">
        {fields.map((f, i) => (
          <div
            key={i}
            draggable
            onDragStart={() => setDraggingIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (draggingIdx !== null && draggingIdx !== i) move(draggingIdx, i); setDraggingIdx(null); }}
            className="flex items-center gap-2 p-2 border border-border rounded-md bg-card"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
            <Input
              value={f.label}
              onChange={(e) => update(i, { label: e.target.value, key: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 60) })}
              className="h-8 text-sm flex-1"
            />
            <select
              value={f.type}
              onChange={(e) => update(i, { type: e.target.value as any })}
              className="h-8 px-2 text-xs border border-input rounded-md bg-background"
            >
              {["text", "date", "phone", "email", "textarea", "checkbox"].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-xs whitespace-nowrap">
              <Checkbox checked={f.required} onCheckedChange={(v) => update(i, { required: !!v })} />
              Required
            </label>
            <Button size="sm" variant="ghost" onClick={() => remove(i)}>
              <Trash2 className="w-3.5 h-3.5 text-destructive" />
            </Button>
          </div>
        ))}
        {fields.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No additional fields. Add fields signers must fill out.</p>
        )}
      </div>
    </div>
  );
}

/* ───────── Preview component ───────── */
function WaiverPreview({ waiver }: { waiver: Partial<WaiverCatalogItem> }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (waiver.source_type === "pdf_upload" && waiver.pdf_storage_path) {
      (async () => {
        const { data } = await supabase.storage.from("waiver-pdfs").createSignedUrl(waiver.pdf_storage_path!, 600);
        setPdfUrl(data?.signedUrl ?? null);
      })();
    }
  }, [waiver.source_type, waiver.pdf_storage_path]);

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold">{waiver.title || "Untitled"}</h2>

      {waiver.source_type === "text" && (
        <RichTextDisplay html={waiver.body_html ?? ""} />
      )}
      {waiver.source_type === "pdf_upload" && (
        pdfUrl
          ? <iframe src={pdfUrl} className="w-full h-96 border border-border rounded" title="Waiver PDF" />
          : <div className="text-sm text-muted-foreground italic">PDF preview unavailable</div>
      )}
      {waiver.source_type === "external_url" && (
        <div className="border border-border rounded p-4 text-sm">
          <p className="text-muted-foreground mb-2">External waiver — signers will be redirected to:</p>
          <a href={waiver.external_url ?? "#"} target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-1">
            {waiver.external_url} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {(waiver.required_fields?.length ?? 0) > 0 && (
        <div className="space-y-3 border-t border-border pt-4">
          <h3 className="font-semibold text-sm">Required information</h3>
          {(waiver.required_fields ?? []).map((f, i) => (
            <div key={i}>
              <label className="text-sm font-medium">
                {f.label} {f.required && <span className="text-destructive">*</span>}
              </label>
              {f.type === "textarea" ? (
                <Textarea disabled placeholder={`(${f.type})`} rows={2} />
              ) : f.type === "checkbox" ? (
                <div className="flex items-center gap-2"><Checkbox disabled /> <span className="text-xs text-muted-foreground">Acknowledge</span></div>
              ) : (
                <Input disabled type={f.type === "date" ? "date" : "text"} placeholder={`(${f.type})`} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-border pt-4">
        <h3 className="font-semibold text-sm mb-2">Signature</h3>
        {waiver.signature_mode === "typed_name" && (
          <Input disabled placeholder="Type your full legal name" className="font-serif italic" />
        )}
        {waiver.signature_mode === "drawn_signature" && (
          <div className="h-24 border-2 border-dashed border-border rounded flex items-center justify-center text-xs text-muted-foreground">
            Sign here (canvas)
          </div>
        )}
        {waiver.signature_mode === "click_to_sign" && (
          <div className="space-y-2">
            <Input disabled placeholder="Type your full legal name" />
            <Button disabled className="w-full">I agree and sign</Button>
          </div>
        )}
      </div>

      <Button disabled className="w-full">Submit (preview)</Button>
    </div>
  );
}
